import React, { useContext, useState } from 'react';
import ReactDOM from 'react-dom';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { GlobalContext } from '../contexts/GlobalContext';
import useAchievementTriggers from '../hooks/useAchievementTriggers';
import LogoutButton from './LoginWithGoogle/LogoutButton';
import TrialBadge from './TrialBadge';

const Tooltip = ({ text, children }) => {
  const tooltipRef = React.useRef(null);
  const [position, setPosition] = React.useState({ top: 0, left: 0 });
  const [isHovered, setIsHovered] = React.useState(false);

  const calculatePosition = () => {
    if (tooltipRef.current) {
      const rect = tooltipRef.current.getBoundingClientRect();
      const topPosition = rect.bottom + window.scrollY + 5;
      const leftPosition = rect.left + window.scrollX + rect.width / 2;

      setPosition({
        top: topPosition,
        left: leftPosition,
      });
    }
  };

  React.useEffect(() => {
    // Delay the initial calculation to ensure the DOM is fully rendered
    const timeoutId = setTimeout(() => {
      calculatePosition();
    }, 100);

    const handleResize = () => {
      calculatePosition();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [tooltipRef.current]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    calculatePosition(); // Recalculate position on hover
  };

  return (
    <span
      className="relative group inline-block"
      ref={tooltipRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      {ReactDOM.createPortal(
        <span
          className={`absolute w-max p-2 text-sm text-black bg-white border rounded-lg shadow-md transition-opacity duration-200 pointer-events-none z-50 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            top: position.top,
            left: position.left,
            transform: 'translateX(-50%)',
          }}
        >
          {text}
        </span>,
        document.body
      )}
    </span>
  );
};

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { completedNodes, trialStatus } = useContext(GlobalContext);
  const [isSpinning, setIsSpinning] = useState(false);
  const location = useLocation();
  const checkAchievements = useAchievementTriggers();
  const navigate = useNavigate();

  const handleImageClick = () => {
    console.log('Spinning cat');
    setIsSpinning(true);

    setTimeout(() => {
      setIsSpinning(false);
      checkAchievements.checkSpinningCat();
    }, 1000);
  };

  const handleClick = (node, event) => {
    if (!completedNodes.includes(node)) {
      // Prevent navigation if the lesson is not completed
      event.preventDefault();
    }
  };

  const handleLogout = () => {
    logout(); // Use AuthContext logout method
    navigate('/');
  };

  return (
    <header className="fixed top-0 left-0 right-0 flex flex-wrap md:justify-start md:flex-nowrap z-50 w-full text-base">
      <nav
        className="h-16 relative w-full bg-white border border-gray-200 py-6 px-4 md:flex md:items-center md:justify-between md:py-0 md:px-6 lg:px-8 xl:mx-auto shadow-xs"
        aria-label="Global"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center flex-1">
            <img
              className={`w-10 h-10 mr-3 cursor-pointer transition-transform duration-1000 ${
                isSpinning ? 'rotate-360' : ''
              }`}
              src="/maincar.png"
              alt="Brand"
              onClick={handleImageClick}
            />
            <a className="flex-none text-xl font-semibold mr-4" href="/" aria-label="Brand">
              internhub
            </a>
            <div className="shrink-0">
              <TrialBadge />
            </div>
          </div>
          <div className="md:hidden">
            <button
              type="button"
              className="hs-collapse-toggle size-8 flex justify-center items-center text-base font-semibold rounded-full border border-gray-200 text-gray-800 hover:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none"
              data-hs-collapse="#navbar-collapse-with-animation"
              aria-controls="navbar-collapse-with-animation"
              aria-label="Toggle navigation"
            >
              <svg
                className="hs-collapse-open:hidden shrink-0 size-4"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="3" x2="21" y1="6" y2="6" />
                <line x1="3" x2="21" y1="12" y2="12" />
                <line x1="3" x2="21" y1="18" y2="18" />
              </svg>
              <svg
                className="hs-collapse-open:block hidden shrink-0 size-4"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div
          id="hs-navbar-header-floating"
          className="hidden hs-collapse overflow-hidden transition-all duration-300 basis-full grow md:block"
          aria-labelledby="hs-navbar-header-floating-collapse"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-2 md:gap-3 mt-3 md:mt-0 py-2 md:py-0 md:ps-7">
            <NavLink
              to="/home"
              onClick={(event) => handleClick('start', event)}
              className={({ isActive }) =>
                completedNodes.includes('start')
                  ? isActive
                    ? 'py-0.5 md:py-2 px-4 md:px-1 border-s-2 md:border-s-0 md:border-b-2 border-blue-500 font-medium text-blue-500 focus:outline-hidden'
                    : 'py-0.5 md:py-2 px-4 md:px-1 border-s-2 md:border-s-0 md:border-b-2 border-transparent text-gray-600 hover:text-gray-400 focus:outline-hidden'
                  : 'py-0.5 md:py-2 px-4 md:px-1 border-s-2 md:border-s-0 md:border-b-2 border-transparent text-gray-400 cursor-not-allowed'
              }
            >
              {completedNodes.includes('start') ? (
                <span>Home</span>
              ) : (
                <Tooltip text="Complete the 'Learn about InternHub' lesson to unlock this feature">
                  <span>Home</span>
                </Tooltip>
              )}
            </NavLink>
            <NavLink
              to="/progress-tree"
              className={({ isActive }) =>
                isActive
                  ? 'py-0.5 md:py-2 px-4 md:px-1 border-s-2 md:border-s-0 md:border-b-2 border-blue-500 font-medium text-blue-500 focus:outline-hidden'
                  : 'py-0.5 md:py-2 px-4 md:px-1 border-s-2 md:border-s-0 md:border-b-2 border-transparent text-gray-600 hover:text-gray-400 focus:outline-hidden'
              }
            >
              Lesson Tree
            </NavLink>
            <NavLink
              to="/job-board"
              onClick={(event) => handleClick('roles', event)}
              className={({ isActive }) =>
                completedNodes.includes('roles')
                  ? isActive
                    ? 'py-0.5 md:py-2 px-4 md:px-1 border-s-2 md:border-s-0 md:border-b-2 border-blue-500 font-medium text-blue-500 focus:outline-hidden'
                    : 'py-0.5 md:py-2 px-4 md:px-1 border-s-2 md:border-s-0 md:border-b-2 border-transparent text-gray-600 hover:text-gray-400 focus:outline-hidden'
                  : 'py-0.5 md:py-2 px-4 md:px-1 border-s-2 md:border-s-0 md:border-b-2 border-transparent text-gray-400 cursor-not-allowed'
              }
            >
              {completedNodes.includes('roles') ? (
                <span>Job Board</span>
              ) : (
                <Tooltip text="Read lesson 'Roles & Positions' in order to unlock this feature">
                  <span>Job Board</span>
                </Tooltip>
              )}
            </NavLink>
            <NavLink
              to="/application-tracking"
              onClick={(event) => handleClick('applying', event)}
              className={({ isActive }) =>
                completedNodes.includes('applying')
                  ? isActive
                    ? 'py-0.5 md:py-2 px-4 md:px-1 border-s-2 md:border-s-0 md:border-b-2 border-blue-500 font-medium text-blue-500 focus:outline-hidden'
                    : 'py-0.5 md:py-2 px-4 md:px-1 border-s-2 md:border-s-0 md:border-b-2 border-transparent text-gray-600 hover:text-gray-400 focus:outline-hidden'
                  : 'py-0.5 md:py-2 px-4 md:px-1 border-s-2 md:border-s-0 md:border-b-2 border-transparent text-gray-400 cursor-not-allowed'
              }
            >
              {completedNodes.includes('applying') ? (
                <span>Application Tracker</span>
              ) : (
                <Tooltip text="Read lesson 'The Perfect Application' in order to unlock this feature">
                  <span>Application Tracker</span>
                </Tooltip>
              )}
            </NavLink>
            {/* <NavLink
              to="/resume-review"
              onClick={(event) => handleClick("resume", event)}
              className={`flex items-center ${
                completedNodes.includes("resume")
                  ? "text-gray-600 hover:text-gray-400"
                  : "text-gray-400 cursor-not-allowed"
              }`}
            >
              {completedNodes.includes("resume") ? (
                <span
                  className={`py-0.5 md:py-2 px-4 md:px-1 border-s-2 md:border-s-0 md:border-b-2 border-transparent`}
                >
                  Resume Review
                </span>
              ) : (
                <Tooltip text="Read lesson 'Resume' in order to unlock this feature">
                  <span
                    className={`py-0.5 md:py-2 px-4 md:px-1 border-s-2 md:border-s-0 md:border-b-2 border-transparent`}
                  >
                    Resume Review
                  </span>
                </Tooltip>
              )}
            </NavLink> */}
            <NavLink
              to="/technical"
              onClick={(event) => handleClick('leetcode', event)}
              className={({ isActive }) =>
                completedNodes.includes('leetcode')
                  ? isActive
                    ? 'py-0.5 md:py-2 px-4 md:px-1 border-s-2 md:border-s-0 md:border-b-2 border-blue-500 font-medium text-blue-500 focus:outline-hidden'
                    : 'py-0.5 md:py-2 px-4 md:px-1 border-s-2 md:border-s-0 md:border-b-2 border-transparent text-gray-600 hover:text-gray-400 focus:outline-hidden'
                  : 'py-0.5 md:py-2 px-4 md:px-1 border-s-2 md:border-s-0 md:border-b-2 border-transparent text-gray-400 cursor-not-allowed'
              }
            >
              {completedNodes.includes('leetcode') ? (
                <span>Technical Prep</span>
              ) : (
                <Tooltip text="Read lesson 'Leetcode' in order to unlock this feature">
                  <span>Technical Prep</span>
                </Tooltip>
              )}
            </NavLink>
            <NavLink
              to="/behavioral-interview"
              onClick={(event) => handleClick('behavioral', event)}
              className={({ isActive }) =>
                completedNodes.includes('behavioral')
                  ? isActive
                    ? 'py-0.5 md:py-2 px-4 md:px-1 border-s-2 md:border-s-0 md:border-b-2 border-blue-500 font-medium text-blue-500 focus:outline-hidden'
                    : 'py-0.5 md:py-2 px-4 md:px-1 border-s-2 md:border-s-0 md:border-b-2 border-transparent text-gray-600 hover:text-gray-400 focus:outline-hidden'
                  : 'py-0.5 md:py-2 px-4 md:px-1 border-s-2 md:border-s-0 md:border-b-2 border-transparent text-gray-400 cursor-not-allowed'
              }
            >
              {completedNodes.includes('behavioral') ? (
                <span>Behavioral Prep</span>
              ) : (
                <Tooltip text="Read lesson 'Behavioral' in order to unlock this feature">
                  <span>Behavioral Prep</span>
                </Tooltip>
              )}
            </NavLink>
            <NavLink
              to="/alumni-finder"
              onClick={(event) => handleClick('networking', event)}
              className={({ isActive }) =>
                completedNodes.includes('networking')
                  ? isActive
                    ? 'py-0.5 md:py-2 px-4 md:px-1 border-s-2 md:border-s-0 md:border-b-2 border-blue-500 font-medium text-blue-500 focus:outline-hidden'
                    : 'py-0.5 md:py-2 px-4 md:px-1 border-s-2 md:border-s-0 md:border-b-2 border-transparent text-gray-600 hover:text-gray-400 focus:outline-hidden'
                  : 'py-0.5 md:py-2 px-4 md:px-1 border-s-2 md:border-s-0 md:border-b-2 border-transparent text-gray-400 cursor-not-allowed'
              }
            >
              {completedNodes.includes('networking') ? (
                <span>{trialStatus?.has_trial ? 'Networking' : 'Alumni'}</span>
              ) : (
                <Tooltip text="Read lesson 'Networking' in order to unlock this feature">
                  <span>{trialStatus?.has_trial ? 'Networking' : 'Alumni'}</span>
                </Tooltip>
              )}
            </NavLink>
            {isAuthenticated && <LogoutButton />}
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
